import { supabase } from '@/integrations/supabase/client';
import type { Notification, CreateNotificationParams } from '@/types/notifications';
import { buildPublicProfileMap } from '@/services/profileUtils';

// Re-export admin functions for backward compatibility
export * from './notificationsAdmin';

// Fetch user notifications
export async function fetchNotifications(limit = 20): Promise<Notification[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const NOTIFICATION_FIELDS = 'id, user_id, batch_id, type, title, message, image_url, action_url, action_label, attachment_url, attachment_name, is_read, scheduled_for, sent_at, created_at, created_by';

  // Single query: non-scheduled OR already-due scheduled notifications
  const { data, error } = await supabase
    .from('notifications')
    .select(NOTIFICATION_FIELDS)
    .eq('user_id', user.id)
    .or(`and(scheduled_for.is.null,or(sent_at.is.null,sent_at.lte.now())),and(scheduled_for.not.is.null,scheduled_for.lte.${new Date().toISOString()})`)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }

  const uniqueNotifications = data || [];

  // Fetch sender names for admin notifications
  const creatorIds = [...new Set(uniqueNotifications
    .filter(n => n.type === 'admin' && n.created_by)
    .map(n => n.created_by as string)
  )];

  let senderMap: Record<string, { display_name: string; avatar_url: string | null }> = {};
  const roleMap: Record<string, string> = {};
  
  if (creatorIds.length > 0) {
    const profileMapObj = await buildPublicProfileMap(creatorIds);
    senderMap = Object.fromEntries(
      Object.entries(profileMapObj).map(([id, p]) => [id, { display_name: p.display_name || '', avatar_url: p.avatar_url }])
    );

    // Sender roles live in the private forjahub schema. The public profile
    // bridge intentionally exposes only presentation data during cutover.
  }

  const enrichedNotifications = uniqueNotifications.map(n => ({
    ...n,
    sender: n.created_by && senderMap[n.created_by] 
      ? { 
          display_name: senderMap[n.created_by].display_name,
          avatar_url: senderMap[n.created_by].avatar_url,
          role: roleMap[n.created_by] || null
        }
      : null
  }));

  return enrichedNotifications as Notification[];
}

// Fetch unread count
export async function fetchUnreadCount(): Promise<number> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return 0;

  const { count, error } = await supabase
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', user.id)
    .eq('is_read', false);

  if (error) {
    console.error('Error fetching unread count:', error);
    return 0;
  }

  return count || 0;
}

// Mark notification as read
export async function markAsRead(notificationId: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('id', notificationId);

  if (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
}

// Mark all notifications as read
export async function markAllAsRead(): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('user_id', user.id)
    .eq('is_read', false);

  if (error) {
    console.error('Error marking all as read:', error);
    throw error;
  }
}

// Delete a single notification
export async function deleteOwnNotification(notificationId: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .delete()
    .eq('id', notificationId);

  if (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
}

// Delete all own notifications
export async function deleteAllOwnNotifications(): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const { error } = await supabase
    .from('notifications')
    .delete()
    .eq('user_id', user.id);

  if (error) {
    console.error('Error deleting all notifications:', error);
    throw error;
  }
}

// Create a notification
export async function createNotification(params: CreateNotificationParams): Promise<Notification> {
  const { data: { user } } = await supabase.auth.getUser();
  
  const { data, error } = await supabase
    .from('notifications')
    .insert({
      ...params,
      created_by: user?.id,
      sent_at: params.scheduled_for ? null : new Date().toISOString(),
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating notification:', error);
    throw error;
  }

  return data as Notification;
}

// Create notification for marketplace events using SECURITY DEFINER function
export async function createMarketplaceNotification(
  userId: string,
  type: 'new_negotiation' | 'new_message' | 'negotiation_completed' | 'negotiation_cancelled',
  data: {
    negotiationId: string;
    listingTitle: string;
    otherUserName?: string;
    messagePreview?: string;
  }
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();

  const configs = {
    new_negotiation: {
      title: '🤝 Nova proposta recebida',
      message: `${data.otherUserName || 'Alguém'} iniciou uma negociação para "${data.listingTitle}"`,
      action_label: 'Ver negociação',
    },
    new_message: {
      title: '💬 Nova mensagem',
      message: data.messagePreview 
        ? `${data.otherUserName || 'Usuário'}: ${data.messagePreview.substring(0, 50)}...`
        : `${data.otherUserName || 'Usuário'} enviou uma mensagem`,
      action_label: 'Responder',
    },
    negotiation_completed: {
      title: '✅ Negociação concluída!',
      message: `A negociação de "${data.listingTitle}" foi finalizada com sucesso`,
      action_label: 'Ver detalhes',
    },
    negotiation_cancelled: {
      title: '❌ Negociação cancelada',
      message: `A negociação de "${data.listingTitle}" foi cancelada`,
      action_label: 'Ver detalhes',
    },
  };

  const config = configs[type];

  try {
    await supabase.rpc('create_marketplace_notification', {
      p_user_id: userId,
      p_title: config.title,
      p_message: config.message,
      p_action_url: `/marketplace/negotiations/${data.negotiationId}`,
      p_action_label: config.action_label,
      p_created_by: user?.id || null
    });
  } catch (error) {
    console.error('Error creating marketplace notification:', error);
  }
}
